import React, { useEffect, useState } from "react";
import "./FIIDIISummaryData.css";
import { environment } from "environment/environment";
import useStockApiWebsocket from "service/websocket/StockApiWebsocket";
import DatePicker from "react-datepicker";
import FIIDIIHistoryData from "../history/FIIDIIHistoryData";

export default function FIIDIISummaryData() {

    const { fiiDiiData } = useStockApiWebsocket(
        environment.stockApiDayRangeDataWSUrl,
        ["fiiDiiData"]
    );

    const [fiiDiiDataList, setFiiDiiDataList] = useState({});
    const [dateList, setDateList] = useState([]);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedData, setSelectedData] = useState(null);
    const [isUserSelected, setIsUserSelected] = useState(false);
    const [openSegment, setOpenSegment] = useState({});
    const [activeTab, setActiveTab] = useState("SUMMARY");

    const [selectedParticipants, setSelectedParticipants] = useState({
        FII: true,
        PRO: true,
        CLIENT: true,
        DII: true
    });

    const SEGMENTS = [
        {
            name: "Index Futures",
            children: ["NIFTY", "BANKNIFTY", "MIDCPNIFTY", "FINNIFTY", "NIFTYNXT50"]
        },
        { name: "Stock Futures" },
        {
            name: "Index Options",
            children: ["Call Options", "Put Options"]
        },
        { name: "Stocks" }
    ];

    const [selectedSegments, setSelectedSegments] = useState(
        SEGMENTS.map(s => s.name)
    );

    /* ================= DATA ================= */
    useEffect(() => {
        if (fiiDiiData && typeof fiiDiiData === "object") {
            const data = fiiDiiData.data || {};
            //setKeyDateList(fiiDiiData.key_list || []);
            setFiiDiiDataList(data);

            const dates = Object.keys(data).sort(
                (a, b) => new Date(b) - new Date(a)
            );

            setDateList(dates);

            if (!isUserSelected && dates.length > 0) {
                setSelectedDate(dates[0]);
                setSelectedData(data[dates[0]]);
            }
            // console.log("Received FII/DII date list :", keyDateList);
            // console.log("Received FII/DII selectedData:", selectedData);
        }
    }, [fiiDiiData]);

    /* ================= HANDLER ================= */
    const handleDateChange = (e) => {
        const date = e.target.value;

        setIsUserSelected(true);
        setSelectedDate(date);
        setSelectedData(fiiDiiDataList[date]);
    };

    const toggleSegment = (participant, segment) => {
        const key = `${participant}-${segment}`;
        setOpenSegment(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // ================= HANDLERS =================
    const handleParticipantChange = (type) => {
        setSelectedParticipants(prev => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    const toggleSegmentFilter = (segmentName) => {
        setSelectedSegments(prev =>
            prev.includes(segmentName)
                ? prev.filter(s => s !== segmentName)
                : [...prev, segmentName]
        );
    };

    const isDropdownAllowed = (participant, seg) => {
        // Only FII for Index Futures
        if (seg.name === "Index Futures") {
            return participant === "FII";
        }

        // All participants for Index Options
        if (seg.name === "Index Options") {
            return true;
        }

        return false;
    };

    /* ================= VALUE FUNCTIONS ================= */

    const getValue = (participant, segment, type) => {
        const p = participant.toLowerCase();

        const futureData = selectedData?.future?.[p];
        const optionData = selectedData?.option?.[p];
        const cashData = selectedData?.cash?.[p];

        if (!futureData && !optionData && !cashData) return "-";

        switch (segment) {

            case "Index Futures":
                return type === "netOi"
                    ? futureData?.futures_outstanding_oi
                    : futureData?.futures_outstanding_net_oi;

            case "Stock Futures":
                return type === "netOi"
                    ? futureData?.futures_stock_outstanding_oi
                    : futureData?.futures_stock_net_oi;

            case "Index Options":
                return type === "netOi"
                    ? optionData?.overall_net_oi
                    : optionData?.overall_net_oi_change;

            case "Stocks":
                return type === "netOi"
                    ? cashData?.futures_stock_outstanding_oi
                    : cashData?.buy_sell_difference;

            default:
                return "-";
        }
    };

    const getOptionChildValue = (participant, child, type) => {
        const p = participant.toLowerCase();
        const data = selectedData?.option?.[p];

        if (!data) return "-";

        if (child === "Call Options") {
            return type === "netOi"
                ? data?.call?.net_oi
                : data?.call?.net_oi_change;
        }

        if (child === "Put Options") {
            return type === "netOi"
                ? data?.put?.net_oi
                : data?.put?.net_oi_change;
        }

        return "-";
    };

    const formatNumber = (num, segName) => {
        if (num == null) return "--";

        const absNum = Math.abs(num);
        const sign = num < 0 ? "-" : "";

        const formatCr = () => {
            const crValue = absNum / 10000000;
            const formatted = crValue.toFixed(2).replace(/\.00$/, "");

            // Handle 0 Cr case
            return (formatted === "0")
                ? sign + absNum.toLocaleString("en-IN") + " Cr"
                : sign + formatted + " Cr";
        };

        // ✅ Stocks → always Cr
        if (segName === "Stocks") {
            return formatCr();
        }

        // Default logic
        if (absNum >= 10000000) {
            return formatCr();
        }

        if (absNum >= 100000) {
            return sign + (absNum / 100000).toFixed(2).replace(/\.00$/, "") + "L";
        }

        return sign + absNum.toLocaleString("en-IN");
    };

    const isSegmentVisible = (participant, segName) => {
        if (segName === "Stocks") {
            return participant === "FII" || participant === "DII";
        }
        return true;
    };

    const getDynamicView = (participant, segName, child = null) => {
        const p = participant.toLowerCase();

        const future = selectedData?.future?.[p];
        const option = selectedData?.option?.[p];
        const cash = selectedData?.cash?.[p];

        /* ================= INDEX FUTURES (CHILD) ================= */
        if (segName === "Index Futures" && child) {
            const qty = future?.["quantity-wise"];
            if (!qty) return null;

            const key = child.toLowerCase();

            return {
                view: qty?.[`${key}_net_view`],
                strength: qty?.[`${key}_net_view_strength`]
            };
        }

        /* ================= INDEX OPTIONS (CHILD) ================= */
        if (segName === "Index Options" && child) {
            if (child === "Call Options") {
                return {
                    view: option?.call?.net_oi_change_view_summary,
                    strength: option?.call?.net_oi_change_view_summary_strength
                };
            }

            if (child === "Put Options") {
                return {
                    view: option?.put?.net_oi_change_view_summary,
                    strength: option?.put?.net_oi_change_view_summary_strength
                };
            }
        }

        /* ================= FUTURES (PARENT) ================= */
        if (segName === "Index Futures") {
            const qty = future?.["quantity-wise"];
            return {
                view: qty?.net_view_summary,
                strength: qty?.net_view_strength_summary
            };
        }

        if (segName === "Stock Futures") {

            return {
                view: future?.futures_stock_net_view_summary,
                strength: future?.futures_stock_net_view_strength_summary
            };
        }

        /* ================= OPTIONS (PARENT) ================= */
        if (segName === "Index Options") {
            return {
                view: option?.overall_net_oi_change_view_summary,
                strength: option?.overall_net_oi_change_view_summary_strength
            };
        }

        /* ================= STOCKS ================= */
        if (segName === "Stocks") {
            return {
                view: cash?.net_view,
                strength: cash?.net_view_strength
            };
        }

        return null;
    };

    // Header Data
    const headerSource = selectedData;
    const headerData = [
        {
            label1: "Date",
            value1: headerSource?.localDate ?? "--",
            label2: "Time",
            value2: headerSource?.localTime ?? "--",
        },
    ];


    /* ================= UI ================= */

    return (
        <div className="fii-dii-data-container">

            {/* HEADER */}
            <header className="header">
                <nav className="nav-container">

                    {/* LEFT SIDE (Logo + DatePicker) */}
                    <div className="left-nav header-flex">
                        <div className="logo-container">
                            <img
                                src="/assets/images/noptionwave-logo-1.png"
                                className="header-logo"
                                alt="logo"
                            />
                        </div>

                        <DatePicker
                            selected={selectedDate ? new Date(selectedDate) : null}
                            onChange={(date) => {
                                const formatted = date.toISOString().split("T")[0];
                                setIsUserSelected(true);
                                handleDateChange({ target: { value: formatted } });
                            }}
                            dateFormat="dd MMM yyyy"
                            className="index-filter"
                            placeholderText="Select Date"
                            maxDate={new Date()}
                            includeDates={dateList.map(d => new Date(d))}
                            popperClassName="custom-datepicker-popper"
                            popperPlacement="bottom-start"
                            portalId="root-portal"
                        />
                    </div>

                    {/* RIGHT SIDE (Header Data) */}
                    <div className="right-nav">
                        {headerData.map((item, idx) => (
                            <div className="header-item" key={idx}>
                                <span className="header-line">
                                    {item.label1} : {item.value1}
                                </span>
                                <span className="header-line">
                                    {item.label2} : {item.value2}
                                </span>
                            </div>
                        ))}
                    </div>

                </nav>
            </header>

            {/* ===== TABS ===== */}
            <div className="tab-container">
                <div className="tab">

                    <button className={`tablinks sub-header-font ${activeTab === "SUMMARY" ? "active" : ""}`}
                        onClick={() => setActiveTab("SUMMARY")}  >
                        SUMMARY
                    </button>

                    <button className={`tablinks sub-header-font ${activeTab === "FUTURES_OPTIONS" ? "active" : ""}`}
                        onClick={() => setActiveTab("FUTURES_OPTIONS")} >
                        FUTURES & OPTIONS
                    </button>
                    <button className={`tablinks sub-header-font ${activeTab === "CASH" ? "active" : ""}`}
                        onClick={() => setActiveTab("CASH")} >
                        CASH
                    </button>
                    <button className={`tablinks sub-header-font ${activeTab === "HISTORY" ? "active" : ""}`}
                        onClick={() => setActiveTab("HISTORY")} >
                        HISTORY
                    </button>

                </div>
            </div>

            {/* 🔥 3 COLUMN LAYOUT */}
            {activeTab === "SUMMARY" && (
                <div className="main-layout">

                    {/* ================= LEFT FILTER ================= */}
                    <div className="left-panel">

                        <div className="filter-header">
                            <h3>Filters</h3>
                            {/* <span onClick={resetFilters}>Reset</span> */}
                        </div>

                        <div className="filter-group">
                            <h4>Participant</h4>
                            {["FII", "DII", "PRO", "CLIENT"].map((type) => (
                                <label key={type}>
                                    <input
                                        type="checkbox"
                                        checked={selectedParticipants[type]}
                                        onChange={() => handleParticipantChange(type)}
                                    />
                                    {type}
                                </label>
                            ))}
                        </div>

                        {/* // ================= UI FILTER ================= */}
                        <div className="filter-group">
                            <h4>Segment</h4>
                            {SEGMENTS.map(seg => (
                                <label key={seg.name}>
                                    <input
                                        type="checkbox"
                                        checked={selectedSegments.includes(seg.name)}
                                        onChange={() => toggleSegmentFilter(seg.name)}
                                    />
                                    {seg.name}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* ================= CENTER TABLE (UNCHANGED) ================= */}
                    <div className="center-panel">

                        <div className="fii-dii-table-container">
                            <div className="table-wrapper">
                                <table className="responsive-table table-bordered text-center sticky-header">
                                    <thead>
                                        <tr>
                                            <th>PARTICIPANT</th>
                                            <th>SEGMENT</th>
                                            <th>BEARISH</th>
                                            <th>BULLISH</th>
                                            <th>NET OI</th>
                                            <th>CHANGE</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {["FII", "PRO", "CLIENT", "DII"]
                                            .filter(participant => selectedParticipants[participant])   // ✅ Participant Filter
                                            .map((participant) => {

                                                // ✅ Segment filter
                                                const visibleSegments = SEGMENTS.filter(seg =>
                                                    selectedSegments.includes(seg.name)
                                                );

                                                let totalRows = visibleSegments.length;
                                                visibleSegments.forEach(seg => {
                                                    const key = `${participant}-${seg.name}`;
                                                    if (openSegment[key] && isDropdownAllowed(participant, seg)) {
                                                        totalRows += seg.children.length;
                                                    }
                                                });

                                                let isFirstRow = true;

                                                return visibleSegments.map((seg) => {
                                                    const key = `${participant}-${seg.name}`;
                                                    const isOpen = openSegment[key];
                                                    const viewData = getDynamicView(participant, seg.name);

                                                    return (
                                                        <React.Fragment key={key}>

                                                            <tr>
                                                                {isFirstRow && (
                                                                    <td rowSpan={totalRows}> {participant} </td>
                                                                )}

                                                                <td
                                                                    className="segment-cell"
                                                                    onClick={() => {
                                                                        if (isDropdownAllowed(participant, seg)) {
                                                                            toggleSegment(participant, seg.name);
                                                                        }
                                                                    }}
                                                                >
                                                                    {seg.name}
                                                                    {isDropdownAllowed(participant, seg) && (
                                                                        <span className="arrow">
                                                                            {isOpen ? "▲" : "▼"}
                                                                        </span>
                                                                    )}
                                                                </td>

                                                                {!viewData?.view ? (
                                                                    <td colSpan="2" className="indecisive">
                                                                        Indecisive
                                                                    </td>
                                                                ) : (
                                                                    <>
                                                                        <td className="bearish-cell">
                                                                            {viewData.view === "BEARISH" && (() => {
                                                                                const strength = viewData.strength?.toLowerCase() || "";

                                                                                let width = "0%";
                                                                                if (strength.includes("strong")) width = "100%";
                                                                                else if (strength.includes("medium")) width = "60%";
                                                                                else if (strength.includes("mild")) width = "45%";

                                                                                return (
                                                                                    <span
                                                                                        className={`badge bearish ${strength}`} style={{ width }}
                                                                                    >
                                                                                        {viewData.strength} Bearish
                                                                                    </span>
                                                                                );
                                                                            })()}
                                                                        </td>

                                                                        <td className="bullish-cell">
                                                                            {viewData.view === "BULLISH" && (() => {
                                                                                const strength = viewData.strength?.toLowerCase() || "";

                                                                                let width = "0%";
                                                                                if (strength.includes("strong")) width = "100%";
                                                                                else if (strength.includes("medium")) width = "60%";
                                                                                else if (strength.includes("mild")) width = "45%";

                                                                                return (
                                                                                    <span
                                                                                        className={`badge bullish ${strength}`}
                                                                                        style={{ width }}
                                                                                    >
                                                                                        {viewData.strength} Bullish
                                                                                    </span>
                                                                                );
                                                                            })()}
                                                                        </td>
                                                                    </>
                                                                )}

                                                                <td>{formatNumber(getValue(participant, seg.name, "netOi"), seg.name)}</td>
                                                                <td>{formatNumber(getValue(participant, seg.name, "change"), seg.name)}</td>
                                                            </tr>

                                                            {isOpen && isDropdownAllowed(participant, seg) &&
                                                                seg.children.map((child, i) => {

                                                                    const viewData = getDynamicView(participant, seg.name, child);

                                                                    return (
                                                                        <tr key={`${key}-${i}`} className="child-row">

                                                                            <td className="child-cell">{child}</td>

                                                                            {!viewData?.view ? (
                                                                                <td colSpan="2" className="indecisive">
                                                                                    Indecisive
                                                                                </td>
                                                                            ) : (
                                                                                <>
                                                                                    <td className="bearish-cell">
                                                                                        {viewData.view === "BEARISH" && (() => {
                                                                                            const strength = viewData.strength?.toLowerCase() || "";

                                                                                            let width = "0%";
                                                                                            if (strength.includes("strong")) width = "100%";
                                                                                            else if (strength.includes("medium")) width = "60%";
                                                                                            else if (strength.includes("mild")) width = "45%";

                                                                                            return (
                                                                                                <span
                                                                                                    className={`badge bearish ${strength}`}
                                                                                                    style={{ width }}
                                                                                                >
                                                                                                    {viewData.strength} Bearish
                                                                                                </span>
                                                                                            );
                                                                                        })()}
                                                                                    </td>

                                                                                    <td className="bullish-cell">
                                                                                        {viewData.view === "BULLISH" && (() => {
                                                                                            const strength = viewData.strength?.toLowerCase() || "";

                                                                                            let width = "0%";
                                                                                            if (strength.includes("strong")) width = "100%";
                                                                                            else if (strength.includes("medium")) width = "60%";
                                                                                            else if (strength.includes("mild")) width = "45%";

                                                                                            return (
                                                                                                <span
                                                                                                    className={`badge bullish ${strength}`}
                                                                                                    style={{ width }}
                                                                                                >
                                                                                                    {viewData.strength} Bullish
                                                                                                </span>
                                                                                            );
                                                                                        })()}
                                                                                    </td>
                                                                                </>
                                                                            )}

                                                                            <td>
                                                                                {seg.name === "Index Options"
                                                                                    ? formatNumber(getOptionChildValue(participant, child, "netOi"), seg.name)
                                                                                    : formatNumber(getValue(participant, seg.name, "netOi"), seg.name)}
                                                                            </td>

                                                                            <td>
                                                                                {seg.name === "Index Options"
                                                                                    ? formatNumber(getOptionChildValue(participant, child, "change"), seg.name)
                                                                                    : formatNumber(getValue(participant, seg.name, "change"), seg.name)}
                                                                            </td>

                                                                        </tr>
                                                                    );
                                                                })
                                                            }

                                                            {(isFirstRow = false)}

                                                        </React.Fragment>
                                                    );
                                                });
                                            })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    </div>

                    {/* ================= RIGHT PANEL ================= */}
                    <div className="right-widget">

                        <div className="card">

                            <div className="card-header">
                                {selectedData?.next_market_open
                                    ? new Date(selectedData.next_market_open).toLocaleDateString("en-GB", {
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric"
                                    }).replace(",", "")
                                    : "--"}
                            </div>

                            <div className="card-body text-center text-muted">
                                Market opens at 9:15am
                            </div>

                        </div>
                        <div className="card">
                            <div className="card-header">
                                {selectedDate
                                    ? new Date(selectedDate).toLocaleDateString("en-GB", {
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric"
                                    })
                                    : "--"}
                            </div>
                            <div className="card-body">

                                {/* NIFTY */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <div className="fw-bold">NIFTY</div>
                                        <div>
                                            {selectedData?.nifty?.toFixed(2)}{" "}
                                            <span className="text-success fw-semibold">
                                                +{selectedData?.nifty_change_percent?.toFixed(2)}%
                                            </span>
                                        </div>
                                    </div>

                                    <div className="border border-primary text-primary px-2 py-1 rounded">
                                        📈
                                    </div>
                                </div>

                                {/* BANKNIFTY */}
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="fw-bold">BANKNIFTY</div>
                                        <div>
                                            {selectedData?.banknifty?.toFixed(2)}{" "}
                                            <span className="text-success fw-semibold">
                                                +{selectedData?.banknifty_change_percent?.toFixed(2)}%
                                            </span>
                                        </div>
                                    </div>

                                    <div className="border border-primary text-primary px-2 py-1 rounded">
                                        📈
                                    </div>
                                </div>

                            </div>

                        </div>
                    </div>

                </div>
            )}
            {/* fii dii history data  */}
            {activeTab === "HISTORY" && (
                <FIIDIIHistoryData />
            )}
            <div className="fixed-footer">
                <div className="footer-left">
                    <span colSpan="15">noptionwave © 2026</span>
                </div>
            </div>
        </div>
    );
}
